/**
 * Unread-notification helpers for the admin web. The bell badge is a pure
 * function of Firestore (mirrors how subscribeTaskAttentionCount derives the
 * task badge) — not a hand-maintained counter.
 *
 * Two-tier read state on each `notifications` doc:
 *   - seen   → surfaced in the bell; drives the BADGE (cleared when the list opens)
 *   - isRead → explicitly opened/tapped; drives bold-vs-read styling
 */
import { useEffect, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

// Live count of UNSEEN notifications for a uid → the bell badge. Two equality
// filters with no orderBy are served by single-field indexes (no composite
// index); capped so a flooded inbox can't stream unbounded.
export const useUnreadCount = (uid) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!uid) {
      setCount(0);
      return undefined;
    }
    const q = query(
      collection(db, "notifications"),
      where("to", "==", uid),
      where("seen", "==", false),
      limit(100),
    );
    const unsub = onSnapshot(
      q,
      (snap) => setCount(snap.size),
      (e) => console.warn("unread-count listener stopped:", e?.message),
    );
    return unsub;
  }, [uid]);
  return count;
};

// Mark a set of notification ids `seen` (clears the badge when the list opens).
// Chunked to stay under the 500-write batch cap.
export const markNotificationsSeen = async (ids = []) => {
  const list = (ids || []).filter(Boolean);
  for (let i = 0; i < list.length; i += 400) {
    const batch = writeBatch(db);
    list
      .slice(i, i + 400)
      .forEach((id) => batch.update(doc(db, "notifications", id), { seen: true }));
    await batch.commit();
  }
};

// Mark EVERY unseen notification for a uid `seen` — query-driven, so the bell
// badge clears fully even though the inbox only renders the latest page (the
// count caps at 100, the list at 50). Call when the inbox opens.
export const markAllNotificationsSeen = async (uid) => {
  if (!uid) return;
  const snap = await getDocs(
    query(
      collection(db, "notifications"),
      where("to", "==", uid),
      where("seen", "==", false),
    ),
  );
  await markNotificationsSeen(snap.docs.map((d) => d.id));
};

// Mark a set of notification ids both seen + read (the "Mark all read" action).
export const markNotificationsRead = async (ids = []) => {
  const list = (ids || []).filter(Boolean);
  for (let i = 0; i < list.length; i += 400) {
    const batch = writeBatch(db);
    list
      .slice(i, i + 400)
      .forEach((id) =>
        batch.update(doc(db, "notifications", id), { seen: true, isRead: true }),
      );
    await batch.commit();
  }
};

// Delete a single notification (used by the per-row Delete action). Kept here so
// every notification mutation lives in one helper.
export const deleteNotification = async (id) => {
  if (!id) return;
  await deleteDoc(doc(db, "notifications", id));
};

// Delete EVERY notification for a uid — the "Clear all" inbox action. Query-
// driven (not just the paged 50 on screen) so the bell empties fully, even for a
// large backlog. Chunked to stay under the 500-write batch cap.
export const deleteAllNotifications = async (uid) => {
  if (!uid) return;
  const snap = await getDocs(
    query(collection(db, "notifications"), where("to", "==", uid)),
  );
  const ids = snap.docs.map((d) => d.id);
  for (let i = 0; i < ids.length; i += 400) {
    const batch = writeBatch(db);
    ids
      .slice(i, i + 400)
      .forEach((id) => batch.delete(doc(db, "notifications", id)));
    await batch.commit();
  }
};

// Where a notification's `details` payload deep-links in the admin app. Returns
// a route string, or null when there's nothing to open (just mark it read).
export const notificationDeepLink = (details) => {
  if (!details || typeof details !== "object") return null;
  // Discussion threads (task / inspection / property / dm / group) open in the
  // Messages page drawer, matched by thread doc id. Checked BEFORE propertyId
  // because an inspection/property discussion notification also carries a
  // propertyId — we want the conversation, not the property detail page.
  if (details.discussionId) return `/messages?thread=${details.discussionId}`;
  if (details.taskId) return "/tasks";
  if (details.propertyId) return `/properties/${details.propertyId}`;
  return null;
};
